import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import Popconfirm from "antd/es/popconfirm";
import { ColumnsType } from "antd/es/table";
import { Download, Lock, Trash2 } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general.ts";
import Panel from "common/NewLook/Panel.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import PanelTable from "features/admin/NewLook/PanelTable";
import Table from "common/Table/Table.tsx";
import Select from "common/Select/Select.tsx";
import Tooltip from "common/Tooltips/Tooltip.tsx";
import Button from "common/Buttons/Button.tsx";
import Text from "common/Typography/Text.tsx";
import useSearchInput from "utils/hooks/useSearchInput";
import useRequestErrorMessage from "features/finance/useRequestErrorMessage";
import { displayResultMessage } from "utils/misc.tsx";
import {
  useDeleteFinanceEntryMutation,
  useGetFinanceEntriesQuery,
  useUpdateFinanceEntryCategoryMutation,
} from "app/services/finance.service";
import {
  FinanceCategory,
  FinanceCurrency,
  FinanceDateRange,
  FinanceDirection,
  FinanceEntriesFilter,
  FinanceEntry,
} from "features/finance/types";
import {
  ALL_SOURCES,
  CATEGORY_TO_LABEL,
  DELETABLE_SOURCES,
  DIRECTION_TO_LABEL,
  FINANCE_ENTRIES_LIMIT,
  SOURCE_TO_LABEL,
} from "features/finance/constants";
import {
  buildCsv,
  downloadTextFile,
  formatMoney,
  getEntryAmount,
  getEntryTotalsRows,
  getFinanceTotals,
  hasEntriesFilter,
} from "features/finance/utils";
import useCategoryOptions from "features/finance/useCategoryOptions";
import FilterChip from "features/finance/FilterChip";

interface FinanceEntriesTableProps {
  range: FinanceDateRange;
  currency: FinanceCurrency;
  filter: FinanceEntriesFilter;
  onFilterChange: (filter: FinanceEntriesFilter) => void;
  day?: string;
  onDayChange: (day?: string) => void;
}

const FinanceEntriesTable = ({
  range,
  currency,
  filter,
  onFilterChange,
  day,
  onDayChange,
}: FinanceEntriesTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const categoryOptions = useCategoryOptions();
  const [searchInput, searchValue] = useSearchInput({
    placeholder: t("finance.entries.search_placeholder"),
    style: { width: 220 },
  });
  const search = useSettledValue(searchValue?.trim() || undefined, 350);

  const entriesRange = day ? { start_date: day, end_date: day } : range;
  const { currentData, isFetching, isError, error } = useGetFinanceEntriesQuery(
    {
      ...entriesRange,
      ...filter,
      search,
      limit: FINANCE_ENTRIES_LIMIT,
    }
  );
  const [updateCategory] = useUpdateFinanceEntryCategoryMutation();
  const [deleteEntry] = useDeleteFinanceEntryMutation();

  useRequestErrorMessage(isError, error);

  const entries = currentData ?? EMPTY_ENTRIES;
  const hasFilters = hasEntriesFilter(filter) || !!search || !!day;
  const totals = useMemo(
    () => getFinanceTotals(getEntryTotalsRows(entries, currency)),
    [entries, currency]
  );

  const clearFilters = () => {
    onFilterChange({});
    onDayChange(undefined);
  };

  const onExport = () => {
    const header = [
      t("finance.entries.columns.date"),
      t("finance.entries.columns.direction"),
      t("finance.entries.columns.source"),
      t("finance.entries.columns.category"),
      t("finance.entries.columns.description"),
      t("finance.entries.columns.original_amount"),
      t("finance.entries.columns.original_currency"),
      t("finance.entries.columns.amount", { currency }),
    ];
    const rows = entries.map((entry) => [
      entry.date,
      t(DIRECTION_TO_LABEL[entry.direction]),
      t(SOURCE_TO_LABEL[entry.source]),
      t(CATEGORY_TO_LABEL[entry.category]),
      entry.description,
      entry.amount,
      entry.currency,
      getEntryAmount(entry, currency),
    ]);
    downloadTextFile(
      `finance-entries-${entriesRange.start_date}-${entriesRange.end_date}.csv`,
      buildCsv([header, ...rows])
    );
  };

  const onCategoryChange = (entry: FinanceEntry, category: FinanceCategory) =>
    displayResultMessage(
      updateCategory({ entry_id: entry.id, category }).unwrap(),
      t("finance.entries.category_updated"),
      undefined,
      undefined,
      undefined,
      undefined,
      true,
      true
    ).catch(() => {});

  const onDelete = (entry: FinanceEntry) =>
    displayResultMessage(
      deleteEntry({ entry_id: entry.id }).unwrap(),
      t("finance.entries.deleted"),
      undefined,
      undefined,
      undefined,
      undefined,
      true,
      true
    ).catch(() => {});

  const columns: ColumnsType<FinanceEntry> = [
    {
      title: t("finance.entries.columns.date"),
      dataIndex: "date",
      width: 116,
      defaultSortOrder: "descend",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "date"),
      render: (date: string) => (
        <Nowrap>{dayjs(date).format("DD MMM YYYY")}</Nowrap>
      ),
    },
    {
      title: t("finance.entries.columns.direction"),
      dataIndex: "direction",
      width: 96,
      render: (direction: FinanceDirection) => (
        <DirectionLabel $isIncome={direction === FinanceDirection.INCOME}>
          {t(DIRECTION_TO_LABEL[direction])}
        </DirectionLabel>
      ),
    },
    {
      title: t("finance.entries.columns.source"),
      dataIndex: "source",
      width: 130,
      render: (source: FinanceEntry["source"]) => (
        <Nowrap>{t(SOURCE_TO_LABEL[source])}</Nowrap>
      ),
    },
    {
      title: t("finance.entries.columns.category"),
      dataIndex: "category",
      width: 230,
      render: (category: FinanceCategory, entry) => (
        <CategoryCell>
          <Select
            size="small"
            value={category}
            options={categoryOptions}
            onChange={(value: FinanceCategory) =>
              onCategoryChange(entry, value)
            }
            popupMatchSelectWidth={false}
            mobileTitle={t("finance.entries.columns.category")}
            style={{ width: 190 }}
          />
          {entry.is_category_locked && (
            <Tooltip title={t("finance.entries.locked")}>
              <LockIcon aria-label={t("finance.entries.locked")}>
                <Lock size={12} strokeWidth={2} />
              </LockIcon>
            </Tooltip>
          )}
        </CategoryCell>
      ),
    },
    {
      title: t("finance.entries.columns.description"),
      dataIndex: "description",
      ellipsis: { showTitle: true },
      render: (description: string) => description,
    },
    {
      title: t("finance.entries.columns.original_amount"),
      dataIndex: "amount",
      align: "right",
      width: 140,
      render: (amount: number, entry) => (
        <Muted>{formatMoney(amount, entry.currency, 2)}</Muted>
      ),
    },
    {
      title: t("finance.entries.columns.amount", { currency }),
      key: "converted",
      align: "right",
      width: 140,
      showSorterTooltip: false,
      sorter: (a, b) =>
        sortByNumberProperty(
          { value: getEntryAmount(a, currency) },
          { value: getEntryAmount(b, currency) },
          "value"
        ),
      render: (_, entry) => {
        const isIncome = entry.direction === FinanceDirection.INCOME;
        const amount = getEntryAmount(entry, currency);
        return (
          <Amount $isIncome={isIncome}>
            {formatMoney(isIncome ? amount : -amount, currency, 2)}
          </Amount>
        );
      },
    },
    {
      key: "actions",
      width: 52,
      align: "right",
      render: (_, entry) =>
        DELETABLE_SOURCES.has(entry.source) && (
          <Popconfirm
            title={t("finance.entries.delete_confirm")}
            okText={t("finance.entries.delete")}
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(entry)}
          >
            <IconButton
              type="text"
              size="small"
              aria-label={t("finance.entries.delete")}
              icon={<Trash2 size={14} strokeWidth={1.75} />}
            />
          </Popconfirm>
        ),
    },
  ];

  return (
    <Panel
      title={t("finance.entries.title")}
      count={currentData ? entries.length : undefined}
      extra={
        !!entries.length && (
          <Totals>
            {!!totals.income && (
              <span>
                {t("finance.entries.totals.income")}{" "}
                <TotalValue>{formatMoney(totals.income, currency)}</TotalValue>
              </span>
            )}
            {!!totals.expenses && (
              <span>
                {t("finance.entries.totals.expenses")}{" "}
                <TotalValue>
                  {formatMoney(totals.expenses, currency)}
                </TotalValue>
              </span>
            )}
            {!!totals.income && !!totals.expenses && (
              <span>
                {t("finance.entries.totals.net")}{" "}
                <TotalValue $signedValue={totals.net}>
                  {formatMoney(totals.net, currency)}
                </TotalValue>
              </span>
            )}
          </Totals>
        )
      }
      actions={
        <Button
          size="small"
          onClick={onExport}
          disabled={!entries.length}
          icon={<Download size={14} strokeWidth={1.75} />}
        >
          {t("finance.entries.export")}
        </Button>
      }
      flush={true}
    >
      <Filters>
        {day && (
          <FilterChip
            clearLabel={t("finance.entries.clear_day")}
            onClear={() => onDayChange(undefined)}
          >
            {dayjs(day).format("ddd, D MMM YYYY")}
          </FilterChip>
        )}
        <Select
          allowClear={true}
          value={filter.direction}
          placeholder={t("finance.entries.all_directions")}
          options={[FinanceDirection.INCOME, FinanceDirection.EXPENSE].map(
            (direction) => ({
              value: direction,
              label: t(DIRECTION_TO_LABEL[direction]),
            })
          )}
          onChange={(direction?: FinanceDirection) =>
            onFilterChange({ ...filter, direction })
          }
          mobileTitle={t("finance.entries.columns.direction")}
          style={{ width: 150 }}
        />
        <Select
          allowClear={true}
          value={filter.source}
          placeholder={t("finance.entries.all_sources")}
          options={ALL_SOURCES.map((source) => ({
            value: source,
            label: t(SOURCE_TO_LABEL[source]),
          }))}
          onChange={(source?: FinanceEntry["source"]) =>
            onFilterChange({ ...filter, source })
          }
          mobileTitle={t("finance.entries.columns.source")}
          style={{ width: 160 }}
        />
        <Select
          allowClear={true}
          value={filter.category}
          placeholder={t("finance.entries.all_categories")}
          options={categoryOptions}
          onChange={(category?: FinanceCategory) =>
            onFilterChange({ ...filter, category })
          }
          popupMatchSelectWidth={false}
          mobileTitle={t("finance.entries.columns.category")}
          style={{ width: 190 }}
        />
        {searchInput}
        {hasFilters && (
          <Button type="link" onClick={clearFilters}>
            {t("finance.entries.clear_filters")}
          </Button>
        )}
      </Filters>
      {entries.length >= FINANCE_ENTRIES_LIMIT && (
        <LimitNote $size="S" type="warning">
          {t("finance.entries.limit_note", { count: FINANCE_ENTRIES_LIMIT })}
        </LimitNote>
      )}
      <PanelTable>
        <Table<FinanceEntry>
          rowKey="id"
          dataSource={entries}
          columns={columns}
          loading={isFetching}
          scroll={{ x: 1000 }}
          itemsType={t("finance.entries.items_type")}
          size="middle"
        />
      </PanelTable>
    </Panel>
  );
};

export default FinanceEntriesTable;

const EMPTY_ENTRIES: FinanceEntry[] = [];

const useSettledValue = <T,>(value: T, wait: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), wait);
    return () => clearTimeout(timeout);
  }, [value, wait]);
  return debounced;
};

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  @media (max-width: 600px) {
    padding: 12px;
    > * {
      flex: 1 1 140px;
    }
  }
`;
const Totals = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px 14px;
  margin-left: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  @media (max-width: 700px) {
    display: none;
  }
`;
const TotalValue = styled.span<{ $signedValue?: number }>`
  font-weight: 500;
  color: ${(p) => directionColor(getNewLookTokens(p.theme), p.$signedValue)};
`;
const LimitNote = styled(Text)`
  padding: 8px 20px 0;
`;
const Nowrap = styled.span`
  white-space: nowrap;
`;
const DirectionLabel = styled.span<{ $isIncome: boolean }>`
  white-space: nowrap;
  color: ${(p) =>
    p.$isIncome
      ? getNewLookTokens(p.theme).up
      : getNewLookTokens(p.theme).text};
`;
const CategoryCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const LockIcon = styled.span`
  display: inline-flex;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Muted = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Amount = styled.span<{ $isIncome: boolean }>`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: ${(p) =>
    p.$isIncome ? getNewLookTokens(p.theme).up : getNewLookTokens(p.theme).ink};
`;
const IconButton = styled(Button)`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
